import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, { useEffect, useCallback, useState } from 'react'
import { useCsrf } from '../../utils/context/CsrfContext'
import CustomForm from './CustomForm'
import { requestInformation } from '../../services/form-api'

const Root = styled(Box)(({ theme }) => ({
  margin: '16px auto',
  maxWidth: '500px',
}))

const LoadingContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(4),
}))

const ErrorContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(4),
  color: theme.palette.error.main,
}))

const fieldStyles = {
  '& input': {
    width: '100%',
    height: '40px',
    marginBottom: '16px',
    borderRadius: '16px',
  },
}

const buttonStyles = {
  width: '100% !important',
}

const formFields = [
  {
    type: 'input',
    props: {
      type: 'email',
      id: 'email',
      name: 'email',
      required: true,
      label: 'Email:',
      autoComplete: 'email',
      freevalidation: 'false',
    },
  },
]

const formCopyStyles = { marginBottom: 2 }

const EmailForm = React.memo(({ emailId }) => {
  const { ensure: ensureCsrf, reset: resetCsrf } = useCsrf()
  const [emailSent, setEmailSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [csrfToken, setCsrfToken] = useState('')
  const initialValues = "React.useMemo(() => ({ email: userEmail || '' }), [userEmail])"
  const formCopy = (() => {
    if (emailId === 0) {
      return "Our standard models are launching soon. Share your email below to be notified as soon as they're available!"
    }
    if (emailId === 1) {
      return "Ready to learn more? Share your email below, and our team will reach out to get started."
    }
    if (emailId === 2) {
      return "Let's get started! Share your email below, and our team will reach out for more information."
    }
    return ''
  })()

  const handleSubmit = useCallback(
    async (formData) => {
      const { email } = formData
      const emailIdToUse = emailId != null ? emailId.toString() : ''
      try {
        const csrf = await ensureCsrf()
        setCsrfToken(csrf)
        setLoading(true)
        await requestInformation(email, emailIdToUse, csrf)
        setEmailSent(true)
      } catch (err) {
        console.error(err.message || 'Login failed. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [emailId, ensureCsrf]
  )

  if (loading) {
    return (
      <Root>
        <LoadingContainer role="status" aria-live="polite">
          <Typography variant="body1">Loading...</Typography>
        </LoadingContainer>
      </Root>
    )
  }

  return (
    <Root>
      <Typography variant="h4" sx={formCopyStyles}>
        {formCopy}
      </Typography>
      <CustomForm
        formId="email-form"
        emailId={emailId}
        isModal={true}
        ctaText="Submit"
        formFields={formFields}
        onSubmit={handleSubmit}
        formCSRF={csrfToken}
        formStyles={fieldStyles}
        buttonStyles={buttonStyles}
        initialValues={initialValues}
      />
    </Root>
  )
})

EmailForm.displayName = 'EmailForm'

EmailForm.propTypes = {
  emailId: PropTypes.number.isRequired,
}

export default EmailForm
